import React, {useEffect, useState} from 'react';
import {ReactComponent as NoticeIcon} from '../../../../../../assets/main/course/notice.svg';
import {ReactComponent as MinusIcon} from '../../../../../../assets/main/course/minus.svg';
import {ReactComponent as PlusIcon} from '../../../../../../assets/main/course/plus.svg';
import {useNavigate, useParams} from 'react-router-dom';
import {Form, Formik, useFormik} from 'formik';
import {message, Select} from 'antd';
import {useInputWithFocus} from "../../../../../../common/hook/useInputWithFocus";
import {ReactComponent as SelectArrayIcon} from '../../../../../../assets/i/select-array-icon.svg';
import {ReactComponent as ArrowUpIconIcon} from '../../../../../../assets/i/arrow-up-icon.svg';
import {useAppDispatch, useAppSelector} from "../../../../../../common/hook";
import {Country, Course, UserCourse} from "../../../../../../features/model/types/courses/curses-by/cursesBy";
import {
    postCursesById
} from "../../../../../../features/model/service/courses/curses-by/post-curses-by-id/postCursesById";
import {
    setRedirectUrlTrackingId
} from "../../../../../../features/model/slice/courses/courses-response-url/coursesResponseUrl";
import usePriceCalculator from "../../../../../../common/hook/use-price-calculator";

const DEFAULT_MIN_LESSONS = 4;


interface BuyCourseData {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    countryId: number | null;
    numberOfLessons: number;
    paymentMethod: string;
}


export const CheckContainer = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {curseId} = useParams();
    const coursesByIdData = useAppSelector(state => state.coursesById.courseById);
    const {errors, error} = useAppSelector(state => state.coursesById);

    const user: UserCourse | undefined = coursesByIdData?.user;

    const countries: Country[] = coursesByIdData?.countries || [];
    const courses: Course[] = coursesByIdData?.courses || [];
    const firstName = user?.firstName || '';
    const lastName = user?.lastName || '';
    const email = user?.email || '';
    const currency = courses[0]?.currency;

    const curseIdNum = Number(curseId);

    const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);

    useEffect(() => {
        const startCurse = courses.filter((el) => el.id === curseIdNum);
        setSelectedCourse(startCurse.length > 0 ? startCurse[0] : null);
    }, [curseIdNum, courses]);

    const selectedCourseStarted = courses.find(course => course.id === curseIdNum);
    const minLessons = selectedCourse?.minLessons ?? selectedCourseStarted?.minLessons ?? DEFAULT_MIN_LESSONS;

    const [lessons, setLessons] = useState(DEFAULT_MIN_LESSONS);

    const [courseTitleOpen, setCourseTitleOpen] = useState(false);
    const [countryOpen, setCountryOpen] = useState(false);

    useEffect(() => {
        setLessons(prev => Math.max(prev, minLessons));
    }, [minLessons]);

    const [isLoading, setIsLoading] = useState(false);

    const [selectedCountry, setSelectedCountry] = useState<number | null>(null);


    useEffect(() => {
        if (coursesByIdData) {
            setSelectedCountry(coursesByIdData?.user.countryId);
        } else {
            setSelectedCountry(null);
        }
    }, [coursesByIdData]);


    const lessonPrice = selectedCourse?.lessonPrice || selectedCourseStarted?.lessonPrice || 0;

    const {
        formattedTotalPrice,
        formattedOriginalPrice
    } = usePriceCalculator(lessons, lessonPrice, selectedCourse?.discount);


    const [paymentMethod, setPaymentMethod] = useState("credit_card");
    const setRedirectUrlTrackingIdData = useAppSelector(state => state.coursesResponseUrl)


    const successMessageError = () => {
        if (error) {
            message.error(error);
        }
    };


    const OnClickCourseTitleHandler = () => {
        setCourseTitleOpen(!courseTitleOpen);
    };

    const OnClickCountryHandler = () => {
        setCountryOpen(!countryOpen);
    };


    const handleLessonsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value.trim();
        if (newValue === '') {
            setLessons(minLessons);
        } else {
            const numericValue = parseInt(newValue, 10);
            if (!isNaN(numericValue)) {
                setLessons(numericValue);
            }
        }
    };

    const handleMinusClick = () => {
        setLessons(prevLessons => Math.max(prevLessons - 1, minLessons));
    };

    const handlePlusClick = () => {
        setLessons(prevLessons => prevLessons + 1);
    };

    const {
        isFilled,
        handleFocus,
        handleBlur,
    } = useInputWithFocus([false, false, false, false, false, false, false, false, false, false]);

    const handleChange = (field: string, value: string) => {
        formik.setFieldValue(field, value);
    };

    const selectedCourseId: number = selectedCourse?.id || selectedCourseStarted?.id || 0;

    const createBuyCourseData = () => {
        return {
            id: selectedCourseId,
            firstName: formik.values.firstName,
            lastName: formik.values.lastName,
            email: formik.values.email,
            countryId: formik.values.countryId,
            numberOfLessons: lessons,
            paymentMethod: paymentMethod,
        };
    };


    const handleSuccessfulPurchase = () => {
        if (setRedirectUrlTrackingIdData.success) {
            if (setRedirectUrlTrackingIdData.redirectUrl === '' && setRedirectUrlTrackingIdData.trackingId) {
                navigate(`/buy-course/${curseId}/bank-transfer/${setRedirectUrlTrackingIdData.trackingId}`);
            }
            if (setRedirectUrlTrackingIdData.redirectUrl) {
                window.location.href = setRedirectUrlTrackingIdData.redirectUrl;
            }
        }
    };


    useEffect(() => {
        handleSuccessfulPurchase()
        dispatch(setRedirectUrlTrackingId({}))
    }, [setRedirectUrlTrackingIdData]);

    const handlePurchaseError = () => {
        successMessageError();
    };


    const handleSubmit = () => {
        setIsLoading(true);

        const buyCourseData = createBuyCourseData();
        dispatch(postCursesById(buyCourseData))
            .unwrap()
            .then((response) => {
                if (response.error) {
                    handlePurchaseError();
                }
            })
            .catch(() => {
                handlePurchaseError();
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const formik = useFormik<BuyCourseData>({
        initialValues: {
            firstName: firstName,
            lastName: lastName,
            email: email,
            id: selectedCourseId,
            countryId: selectedCountry,
            numberOfLessons: lessons,
            paymentMethod: paymentMethod,
        },
        enableReinitialize: true,
        onSubmit: handleSubmit,
    });

    const handleCountryChange = (value: number) => {
        setSelectedCountry(value);
    };

    useEffect(() => {
        const defaultCountry = countries.find(country => country.code === 'BY');
        if (defaultCountry) {
            setSelectedCountry(defaultCountry.id);
        }
    }, [countries]); // Обновляем значение, если список стран изменится


    return (
        <div className="description-form-box">
            {selectedCourse || selectedCourseStarted ? (
                <div className="form-description">
                    <p className="h4">{selectedCourse ? selectedCourse.title : selectedCourseStarted?.title}</p>
                    <div className="body-text-course-none">
                        <div
                            dangerouslySetInnerHTML={{__html: selectedCourse ? selectedCourse.description : selectedCourseStarted?.description || ''}}/>
                    </div>
                </div>
            ) : null}

            <Formik initialValues={{}} onSubmit={handleSubmit}>
                <Form className="form">
                    <div className="fields-row two-cols">
                        <div
                            className={`field has-icon-inp ${isFilled[0] ? "filled" : ""} ${errors.firstName ? 'error-field' : ''}`}>
                            <label
                                className={`field-name ${user?.firstName ? 'active-label' : ''}`}>Имя*</label>
                            <div className="inp-box">
                                <input
                                    type="text"
                                    name="firstName"
                                    disabled={true}
                                    className="form-control"
                                    value={user?.firstName}
                                    onChange={formik.handleChange}
                                    onFocus={() => handleFocus(0)}
                                    onBlur={handleBlur}
                                />
                            </div>
                            {errors.firstName && <p className="field-error">{errors.firstName}</p>}
                        </div>
                        <div
                            className={`field has-icon-inp ${isFilled[1] ? "filled" : ""} ${errors.lastName ? 'error-field' : ''}`}>
                            <label
                                className={`field-name ${user?.lastName ? 'active-label' : ''}`}>Фамилия*</label>
                            <div className="inp-box">
                                <input
                                    type="text"
                                    name="lastName"
                                    disabled={true}
                                    className="form-control"
                                    value={user?.lastName}
                                    onChange={formik.handleChange}
                                    onFocus={() => handleFocus(1)}
                                    onBlur={handleBlur}
                                />
                            </div>
                            {errors.lastName && <p className="field-error">{errors.lastName}</p>}
                        </div>
                    </div>
                    <div className="fields-row">
                        <div
                            className={`field has-icon-inp ${isFilled[2] ? "filled" : ""} ${errors.email ? 'error-field' : ''}`}>
                            <label
                                className={`field-name ${user?.email || formik.values.lastName ? 'active-label' : ''}`}>Email*</label>
                            <div className="inp-box">
                                <input
                                    type="text"
                                    name="email"
                                    className="form-control"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onFocus={() => handleFocus(2)}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>
                        {errors.email && <p className="field-error">{errors.email}</p>}
                    </div>
                    <div className="fields-row two-cols">
                        <div className="field required auto-width">

                            <div style={{width: '100%'}} className="field position">
                                <div  className="course-type type_course"
                                     onClick={OnClickCourseTitleHandler}>
                                    <span
                                        className={formik.values.id || selectedCourseStarted?.title ? 'active-label padding-top' : ''}>Название курса*
                                    </span>
                                    <Select
                                        style={{width: '100%'}}
                                        value={selectedCourse ? selectedCourse.title : selectedCourseStarted?.title}
                                        options={courses.map((course) => ({
                                            label: course.title,
                                            value: course.id.toString(),
                                        }))}
                                        onFocus={() => handleFocus(3)}
                                        onBlur={handleBlur}
                                        onChange={(value) => {
                                            const selectedCourse = courses.find((course) => course.id.toString() === value);
                                            if (selectedCourse) {
                                                setSelectedCourse(selectedCourse);
                                                OnClickCourseTitleHandler();
                                            }
                                        }}
                                        className={`course-type ${selectedCourse ? 'active-label' : ''}`}
                                        onClick={OnClickCourseTitleHandler}
                                        onMouseDown={(e) => e.preventDefault()}
                                        suffixIcon={courseTitleOpen ? <ArrowUpIconIcon/> : <SelectArrayIcon/>}
                                    />
                                </div>
                            </div>
                            {errors.numberOfLessons && <p className="field-error">{errors.numberOfLessons}</p>}
                        </div>

                        <div className="field required small-w">
                            <label className="field-name active-label">Занятия</label>
                            <div className="inp-box">
                                <input
                                    type="number"
                                    className="form-control"
                                    value={lessons === 0 ? '' : lessons}
                                    min={minLessons}
                                    max={100}
                                    onChange={handleLessonsChange}
                                    readOnly
                                />
                                <div className="plus-minus-btns">
                                     <span className="num-btn minus" onClick={handleMinusClick}>
                                            <MinusIcon/>
                                     </span>
                                    <span className="num-btn plus" onClick={handlePlusClick}>
                                        <PlusIcon/>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="fields-row">
                        <div className="field required auto-width">
                            <div style={{width: '100%'}}
                                 className={`field position ${errors.countryId ? 'error-field-border' : ''}`}>
                                <div className="course-type" onClick={OnClickCountryHandler}>
                                    <span
                                        className={(formik.values.countryId ?? selectedCountry) ? 'active-label' : ''}>Страна*</span>
                                    <Select
                                        style={{width: '100%'}}
                                        status={`${errors.countryId ? 'error' : ''}`}
                                        options={countries.map((country) => ({
                                            label: country.title,
                                            value: country.id,
                                        }))}
                                        onFocus={() => handleFocus(4)}
                                        onBlur={handleBlur}
                                        onChange={(value) => {
                                            handleCountryChange(value); // Обновление выбранной страны
                                        }}
                                        value={selectedCountry} // Устанавливаем значение по умолчанию
                                        suffixIcon={countryOpen ? <ArrowUpIconIcon/> : <SelectArrayIcon/>}
                                    />
                                </div>
                            </div>
                            {errors.countryId && <p className="field-error">{errors.countryId}</p>}
                        </div>
                    </div>
                    <div className="fields-row">
                        <div className="field">
                            <div className="radio-group in-cols">
                                <div className="radio-item">
                                    <input
                                        id="payment-method-inp1"
                                        name="payment-method"
                                        type="radio"
                                        value="credit_card"
                                        checked={paymentMethod === "credit_card"}
                                        onChange={() => setPaymentMethod("credit_card")}
                                    />
                                    <label htmlFor="payment-method-inp1">Оплата по карте</label>
                                </div>
                                <div className="radio-item">
                                    <input
                                        id="payment-method-inp2"
                                        name="payment-method"
                                        type="radio"
                                        value="bank_transfer"
                                        checked={paymentMethod === "bank_transfer"}
                                        onChange={() => setPaymentMethod("bank_transfer")}
                                    />
                                    <label htmlFor="payment-method-inp2">Оплата банковским переводом</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="price-btn-box">
                        <div className="total-price">
                            <p className="price h3">{formattedTotalPrice} {currency}</p>
                            {(selectedCourse?.discount !== 0) && (
                                <div className="price-sub-data">
                                    {selectedCourse?.discount !== undefined ? (
                                        <span>Скидка {selectedCourse?.discount} %</span>
                                    ) : (
                                        <span>Скидка {user?.discount} %</span>
                                    )}
                                    {selectedCourse?.discount !== 0 && (
                                        <span className="line-through">{formattedOriginalPrice} {currency}</span>
                                    )}
                                </div>
                            )}
                        </div>
                        <button className="btn" type="submit" disabled={isLoading} onClick={handleSubmit}>
                            {isLoading ? 'Загрузка...' : 'Продолжить'}
                        </button>
                    </div>
                </Form>
            </Formik>
        </div>
    );
};


// {(user?.discount !== null || selectedCourse?.discount !== 0 || user?.discount !== 0) && (
//     <div className="price-sub-data">
//         <span>Скидка {selectedCourse?.discount} %</span>
//         <span className="line-through">{formattedOriginalPrice} BYN</span>
//     </div>
// )}

// <div className="account-tooltip">
//                                         <span className="icon pointer-elem">
//                                             <NoticeIcon />
//                                         </span>
//     <div className="tooltip-text">Tooltip Text Description</div>
// </div>

